// The reader's own 1:1 with the facilitator (D77).
//
// Read and posted to independently of the group conversation's `usePlan` pipeline, but
// under the same discipline: a load replaces the message list rather than merging into it,
// and posting inserts only the row the server returned (`afterPost`). There is no second
// signal stream here — `refreshOn` is `usePlan`'s own snapshot, which already changes on
// every signal and poll tick, so this thread is re-read on the same cadence the group
// conversation is without opening a connection of its own.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { Message, Thread } from '../api/contract.js';
import { afterPost, readMessagePages } from './snapshot.js';

export type PrivateThreadState = {
  readonly messages: readonly Message[];
  /** The first page of this thread's messages has arrived. False again on a thread change. */
  readonly loaded: boolean;
  readonly nextBefore: string | null;
  readonly posting: boolean;
  readonly sendFailed: boolean;
  loadOlder: () => void;
  post: (body: string) => Promise<void>;
};

export function usePrivateThread(
  client: ApiClient,
  thread: Thread | null,
  refreshOn: unknown,
): PrivateThreadState {
  const [messages, setMessages] = useState<readonly Message[]>(Object.freeze([]));
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [posting, setPosting] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const depth = useRef(1);
  const alive = useRef(true);
  const threadId = thread?.id ?? null;

  // Every call claims the next generation before it awaits anything, and a completion is
  // believed only if it is still current when it lands. This is what makes the ordering of
  // responses irrelevant: whichever call was issued LAST always holds the highest number,
  // so an older thread's slower response can never overtake it, no matter which of the two
  // requests the network finishes first. Without this, a load for a private thread that has
  // since been swapped out from under it — a route change to another plan, a change of
  // thread, a refresh tick overtaken by a newer one — can land after the reset below and
  // write a stranger's conversation onto this screen.
  const generation = useRef(0);

  // Same idea, a separate counter, for the send path. It cannot share `generation` above:
  // a refresh tick calls `load()` on the same cadence a send's network round trip takes, so
  // sharing one counter would invalidate an in-flight send's own completion for no reason
  // other than an unrelated poll landing in between. This one advances on a thread change
  // AND on every `post()` call, which is what a thread-id comparison alone got wrong: after
  // an A→B→A switch, a stale send from the first visit to A recaptures the "current" thread
  // id and can splice its late completion into, or clear the composer state for, a second,
  // genuinely current send on that same thread. A generation captured at the moment a send
  // is issued has no such collision — only the most recently issued send on any thread ever
  // matches what's current when it completes.
  const sendGeneration = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (threadId === null) {
      // Nothing to read, but the claim still happens: a load in flight for whichever
      // thread was here before must be retired, or its late response would pass the
      // check below and repaint the old thread's messages after the reset cleared them.
      generation.current += 1;
      return;
    }
    const mine = (generation.current += 1);
    try {
      const pages = await readMessagePages(client, threadId, depth.current);
      if (!alive.current || mine !== generation.current) {
        return;
      }
      depth.current = pages.depth;
      setMessages(pages.messages);
      setNextBefore(pages.nextBefore);
      setLoaded(true);
    } catch {
      // A transcript that has been read once stays on screen, same rule as `usePlan`'s
      // snapshot. Every caller fires this as `void load()`, so a rejection escaping here
      // is an unhandled one; swallowing it changes nothing on screen, and the refresh
      // cadence this hook piggybacks on retries the read on its next tick.
    }
  }, [client, threadId]);

  // A different thread — a different plan, or the reader's own thread finally arriving in
  // the snapshot — is a fresh everything, same as a change of plan resets `usePlan`'s depth.
  // `posting`/`sendFailed` reset here too: a send in flight on the thread being left behind
  // is that thread's problem now, not this one's, so its composer starts clean rather than
  // inheriting a stuck "sending" or a stale failure from whatever was happening before.
  useEffect(() => {
    depth.current = 1;
    setMessages(Object.freeze([]));
    setNextBefore(null);
    setLoaded(false);
    setPosting(false);
    setSendFailed(false);
    // Retires whatever send was in flight for the thread being left behind, so its
    // completion can never match a send issued after this point — including a later send
    // back on this very same thread id.
    sendGeneration.current += 1;
    void load();
  }, [threadId, load]);

  // Piggybacks on the group conversation's own refresh cadence rather than polling or
  // streaming a second time; see the header. Runs alongside the reset effect above on a
  // thread change too — one redundant read, never a stale or merged one.
  useEffect(() => {
    void load();
  }, [refreshOn, load]);

  const loadOlder = useCallback(() => {
    if (nextBefore === null) {
      return;
    }
    depth.current += 1;
    void load();
  }, [nextBefore, load]);

  const post = useCallback(
    async (body: string): Promise<void> => {
      if (threadId === null) {
        return;
      }
      // Claimed before the POST goes out, same discipline as `load`'s `mine`. Every site
      // below that mutates state after the `await` checks this instead of the thread id —
      // see the comment on `sendGeneration` above for why thread id alone isn't enough.
      const mySend = (sendGeneration.current += 1);
      setPosting(true);
      setSendFailed(false);
      try {
        const stored = await client.postMessage(threadId, body);
        // The route can move to another plan, this hook can be swapped onto the reader's
        // own thread arriving fresh, or a second send can be issued on this very thread,
        // while the POST is still in flight. Splicing that response into whatever is on
        // screen now — or running the reload it would trigger, which claims the newest
        // `load` generation and wins that guard too — writes a stale message into the
        // wrong conversation. Neither happens unless this is still the most recent send.
        if (alive.current && sendGeneration.current === mySend) {
          setMessages((current) => afterPost(current, stored));
          void load();
        }
      } catch {
        // Same guard as the success path: a rejection for a send the screen has already
        // moved past must not paint a failure onto whatever send is current now.
        if (alive.current && sendGeneration.current === mySend) {
          setSendFailed(true);
        }
        throw new Error('the message was not sent');
      } finally {
        // And the reset effect above already zeroed `posting` for a thread change, and a
        // newer send already re-set it to `true` for its own attempt — clearing it again
        // here would re-enable a composer that has moved on to a different send of its own.
        if (alive.current && sendGeneration.current === mySend) {
          setPosting(false);
        }
      }
    },
    [client, threadId, load],
  );

  return { messages, loaded, nextBefore, posting, sendFailed, loadOlder, post };
}
